import { AsyncSubject, of as observableOf, of } from 'rxjs';

import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Observable, BehaviorSubject } from 'rxjs';
import * as _ from 'lodash';
import { BaseService } from './baseService';
import { ApiMode, ApplicationType } from '../../utils/enums';
import { OfflineApiService } from './offlineApi.service';
import { AppConstants } from '../../utils/appConstants.class';
import { AppVersion, HomeButton, InspectionWorkflow } from '../models';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root'
})
export class ApplicationService extends BaseService {
  public applicationTypes: any[];
  public inspectionWorkflows: { [key: number]: { [key: number]: InspectionWorkflow } };

  private environmentVariables: AsyncSubject<any> = null;
  private newApplicationVersion: AsyncSubject<AppVersion> = null;
  private homeButtons: AsyncSubject<HomeButton[]> = null;

  constructor(private apiService: ApiService, private offlineApi: OfflineApiService, private config: ConfigService) {
    super();
  }

  private currentApplicationType = null;

  public getCurrent(): Observable<any> {
    if (!this.isConnection) {
      const applicationType = localStorage.getItem(`${AppConstants.OFFLINE_INSPECTION_APP_TYPE}_${this.currentUserId}`)
        ? +localStorage.getItem(`${AppConstants.OFFLINE_INSPECTION_APP_TYPE}_${this.currentUserId}`)
        : +localStorage.getItem(`${AppConstants.CURRENT_APP_TYPE}_${this.currentUserId}`);

      this.currentApplicationType = applicationType;
    }

    if (this.currentApplicationType != null) {
      return observableOf(this.currentApplicationType);
    }

    const url = '/applications/current';
    return this.apiService.get(url).pipe(
      map((res) => {
        this.currentApplicationType = res;
        sessionStorage.setItem(AppConstants.CURRENT_APP_TYPE, res);
        localStorage.setItem(`${AppConstants.CURRENT_APP_TYPE}_${this.currentUserId}`, res);
        return res;
      })
    );
  }

  public setApplicationType(current: number) {
    const url = `/applications/current`;
    return this.apiService.post(url, current).pipe(
      map((res) => {
        this.currentApplicationType = res;
        sessionStorage.setItem(AppConstants.CURRENT_APP_TYPE, res);
        localStorage.setItem(`${AppConstants.CURRENT_APP_TYPE}_${this.currentUserId}`, res);
        return res;
      })
    );
  }

  getValue(value: number): string {
    const obj = _.find(this.getApplicationTypes(), ['Value', value]);
    return obj ? obj.Label : '';
  }

  getApplicationTypes() {
    if (this.applicationTypes != null) {
      return new BehaviorSubject(this.applicationTypes).asObservable();
    }
    const url = '/applications';

    return this.offlineApi.getWithMode(url, ApiMode.Offline, AppConstants.INDEXEDDB_TABLES.applicationTypes).pipe(
      map((res: any) => {
        this.applicationTypes = res;
        return res;
      })
    );
  }

  isOptimization(): Observable<boolean> {
    return this.getCurrent().pipe(
      map((el) => {
        return el === ApplicationType.Optimization;
      })
    );
  }

  isMaintenance(): Observable<boolean> {
    return this.getCurrent().pipe(
      map((el) => {
        return el === ApplicationType.Maintenance;
      })
    );
  }

  isInstallation(): Observable<boolean> {
    return this.getCurrent().pipe(
      map((el) => {
        return el === ApplicationType.Installation;
      })
    );
  }

  getNewApplicationVersion(): Observable<AppVersion> {
    if (!this.isConnection) return of(null);

    if (this.newApplicationVersion) {
      return this.newApplicationVersion.asObservable();
    }

    this.newApplicationVersion = new AsyncSubject<AppVersion>();
    this.apiService.get('/applications/newversion').subscribe(
      (res) => {
        this.newApplicationVersion.next(res);
        this.newApplicationVersion.complete();
      },
      (err) => {
        this.newApplicationVersion.error(err);
      }
    );

    return this.newApplicationVersion.asObservable();
  }

  getHomeButtons(): Observable<HomeButton[]> {
    if (!this.isConnection) return of(null);

    if (this.homeButtons) {
      return this.homeButtons.asObservable();
    }

    this.homeButtons = new AsyncSubject<HomeButton[]>();
    this.apiService.get('/applications/homebuttons').subscribe(
      (res) => {
        this.homeButtons.next(res);
        this.homeButtons.complete();
      },
      (err) => {
        this.homeButtons.error(err);
      }
    );

    return this.homeButtons.asObservable();
  }

  getInspectionWorkflow(): Observable<{
    [key: number]: InspectionWorkflow;
  }> {
    if (this.inspectionWorkflows != null) {
      return new BehaviorSubject(this.inspectionWorkflows[this.currentApplicationType]).asObservable();
    }
    return this.isConnection
      ? this.config.getResource('configurations/inspectionworkflow.json').pipe(
          map((res) => {
            this.inspectionWorkflows = res;
            return res[this.currentApplicationType];
          })
        )
      : of(null);
  }

  getEnvironmentVariables(): Observable<any> {
    if (this.environmentVariables) {
      return this.environmentVariables.asObservable();
    }

    this.environmentVariables = new AsyncSubject<any>();

    this.config.getResource('configurations/environment-variables.json').subscribe(
      (res) => {
        this.environmentVariables.next(res);
        this.environmentVariables.complete();
      },
      (err) => {
        this.environmentVariables.error(err);
      }
    );

    return this.environmentVariables.asObservable();
  }

}
